import { useTranslation } from 'next-i18next'
import React from 'react'
import Container from 'react-bootstrap/Container'
import Nav from 'react-bootstrap/Nav'
import Navbar from 'react-bootstrap/Navbar'

import { BeltVisual } from '@goodfellas/belt-visual'
import { Colors } from '@goodfellas/graduation'

import { useAuth } from '../contexts/AuthContext'
import { normalizeBeltColor, normalizeDegree } from '../functions/graduation'
import { useStore } from '../functions/store'
import type { User as StoreUser } from '../functions/store'
import GraduationModal from './360-feedback/graduation/GraduationModal'
import Button from './button'

type NavigationMenuProps = {
  user: StoreUser | null
  isAvaliacao360?: boolean
}

// Logout icon for mobile
const LogoutIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
)

function NavigationMenu({ user, isAvaliacao360 }: NavigationMenuProps) {
  const { t } = useTranslation('common')
  const { logout } = useAuth()
  const userStore = useStore((s) => s)
  const [isGradOpen, setIsGradOpen] = React.useState(false)
  const [expanded, setExpanded] = React.useState(false)
  const navbarRef = React.useRef<HTMLDivElement | null>(null)

  const beltColor = normalizeBeltColor(user?.belt)
  const degree = normalizeDegree(beltColor, user?.degree)
  const groupId = userStore.group?._id ? String(userStore.group._id) : ''

  // Truncate name for mobile
  const displayName = user?.name || 'visitante'
  const truncatedName =
    displayName.length > 12 ? `${displayName.substring(0, 12)}...` : displayName

  // suppressHydrationWarning: avoids "Extra attributes from the server: bis_skin_checked"
  // when browser extensions (e.g. Better Internet Search) inject attributes into the nav DOM.
  React.useEffect(() => {
    if (!expanded) return

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const el = navbarRef.current
      if (!el) return
      if (event.target instanceof Node && el.contains(event.target)) return
      setExpanded(false)
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [expanded])

  return (
    <div suppressHydrationWarning ref={navbarRef}>
      <Container fluid className="navbar-wrapper px-2 px-md-3">
        <Navbar
          className="custom-navbar bg-white"
          collapseOnSelect
          expand="lg"
          expanded={expanded}
          onToggle={(nextExpanded) => setExpanded(Boolean(nextExpanded))}
        >
          <Navbar.Brand href="/">
            <img
              src="/images/logos/goodfellas-h-primary.svg"
              alt="Goodfellas"
              className="navbar-logo"
              width={200}
              height={56}
            />
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" />
          <Navbar.Collapse id="responsive-navbar-nav" suppressHydrationWarning>
            <Nav
              className="ms-auto pb-3 pb-lg-0 gap-2 align-items-lg-center text-uppercase fw-bold"
              suppressHydrationWarning
              onSelect={() => setExpanded(false)}
            >
              {isAvaliacao360 ? (
                user?.phoneNumber ? (
                  <>
                    <GraduationModal
                      isOpen={isGradOpen}
                      phoneNumber={String(user.phoneNumber)}
                      groupId={groupId}
                      initialName={String(user.name || '')}
                      initialType={String(user.type || '')}
                      initialBelt={(beltColor as Colors) || Colors.White}
                      initialDegree={degree}
                      onClose={() => setIsGradOpen(false)}
                      onSaved={({ belt, degree }) => {
                        if (!user) return
                        // Keep both navbar user and global store consistent
                        user.setUser({
                          ...user,
                          belt,
                          degree,
                        })
                        userStore.setUser({
                          ...userStore,
                          belt,
                          degree,
                        })
                      }}
                    />

                    {/* Mobile Layout */}
                    <div className="lg:hidden flex flex-col items-center gap-3 w-full py-3">
                      <div className="flex items-center gap-2 flex-wrap justify-center">
                        <span className="text-sm font-semibold">
                          Osú, {truncatedName}
                        </span>
                        {beltColor && (
                          <button
                            type="button"
                            onClick={() => setIsGradOpen(true)}
                            className="rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/40 hover:opacity-95 transition-opacity"
                            title="Editar graduação"
                            disabled={!groupId}
                          >
                            <BeltVisual
                              color={beltColor as Colors}
                              degree={degree}
                              size="sm"
                              animated
                            />
                          </button>
                        )}
                      </div>
                      <Button
                        label={t('menu.logout')}
                        color="red"
                        size="small"
                        fn={() => {
                          setExpanded(false)
                          logout()
                        }}
                        iconLeft={<LogoutIcon />}
                      />
                    </div>

                    {/* Desktop Layout - Single row aligned */}
                    <div className="hidden lg:flex items-center gap-3">
                      <span className="text-base font-semibold whitespace-nowrap">
                        Osú, {displayName}
                      </span>
                      {beltColor && (
                        <button
                          type="button"
                          onClick={() => setIsGradOpen(true)}
                          className="rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/40 hover:opacity-95 transition-opacity"
                          title="Editar graduação"
                          disabled={!groupId}
                        >
                          <BeltVisual
                            color={beltColor as Colors}
                            degree={degree}
                            size="md"
                            animated
                          />
                        </button>
                      )}
                      <Button
                        label={t('menu.logout')}
                        color="red"
                        size="small"
                        fn={() => {
                          setExpanded(false)
                          logout()
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-end me-4">
                      <p className="opacity-70 cursor-default">
                        {t('menu.feedback_title')}
                      </p>
                    </div>
                  </>
                )
              ) : (
                <>
                  <Nav.Link href="/#beneficios">
                    {t('menu.what_is_it')}
                  </Nav.Link>
                  <Nav.Link href="/#como-funciona">
                    {t('menu.how_it_works')}
                  </Nav.Link>
                  <Nav.Link
                    href="https://jiulia.goodfellas.app"
                    target="_blank"
                  >
                    {t('menu.jiulia')}
                  </Nav.Link>
                  <Nav.Link href="/#faq">{t('menu.faq')}</Nav.Link>
                  <Button
                    href="/#criar-conta"
                    label={t('menu.create_profile')}
                    color="red"
                    size="small"
                    className="mx-3 mb-0 self-center"
                  />
                  <Button
                    href="/avaliacao"
                    label={t('menu.login')}
                    color="transparent"
                    size="small"
                    className="ms-3 mb-0 self-center"
                  />
                </>
              )}
            </Nav>
          </Navbar.Collapse>
        </Navbar>
      </Container>
    </div>
  )
}

export default NavigationMenu
